import { z } from 'zod';
/**
 * Контракты Jira-модуля (M13–M16, Phase 2).
 */
// ─── PUT /api/auth/me/jira-token ─────────────────────────────────────────
export const SetJiraTokenRequestSchema = z.object({
    token: z.string().min(1, 'Токен не может быть пустым'),
});
// ─── GET /api/jira/ping response ─────────────────────────────────────────
export const JiraPingResponseSchema = z.discriminatedUnion('ok', [
    z.object({
        ok: z.literal(true),
        user: z.object({
            name: z.string(),
            displayName: z.string(),
        }),
    }),
    z.object({
        ok: z.literal(false),
        error: z.string(),
    }),
]);
// ─── Jira project (proxy) ────────────────────────────────────────────────
export const JiraProjectSchema = z.object({
    id: z.string(),
    key: z.string(),
    name: z.string(),
});
// ─── Jira issue type (proxy) ─────────────────────────────────────────────
export const JiraIssueTypeSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
    subtask: z.boolean(),
});
// ─── Jira field meta (create meta, proxy) ────────────────────────────────
export const JiraFieldMetaSchema = z.object({
    key: z.string(),
    name: z.string(),
    required: z.boolean(),
    schema: z.object({
        type: z.string(),
        items: z.string().optional(),
        custom: z.string().optional(),
    }),
    allowedValues: z
        .array(z.object({
        id: z.string(),
        value: z.string().optional(),
        name: z.string().optional(),
    }))
        .optional(),
});
// ─── Jira Sync Config ────────────────────────────────────────────────────
export const JiraFieldMappingEntrySchema = z.object({
    activitiesFieldSlug: z.string(),
    jiraField: z.string(),
    transform: z.enum(['none', 'reference_key', 'markdown_to_jira_wiki', 'status_map', 'bool_map']).default('none'),
    /**
     * Колонка справочника, чьё значение уходит в Jira (только для transform=reference_key).
     * Не задано/пусто → ключевая колонка справочника (поведение по умолчанию).
     * Если выбранная колонка сама имеет тип reference (ссылка на другую запись —
     * например «Тех. лид» в справочнике сотрудников), значение дорезолвится до
     * ключевой колонки целевого справочника (логина).
     */
    referenceColumnSlug: z.string().optional(),
    /** Источник значения: поле Lens (по умолчанию) или фиксированное значение. */
    source: z.enum(['field', 'fixed']).default('field'),
    /** Фиксированное значение — используется, когда source='fixed'. */
    fixedValue: z.string().optional(),
    /**
     * Маппинг ключа значения → значение Jira:
     *  - status_map: Lens optionId → Jira status name;
     *  - bool_map: 'true'/'false' → значение Jira-опции (например «Штат»/«Вендор»).
     */
    valueMap: z.record(z.string(), z.string()).optional(),
});
export const JiraFieldMappingSchema = z.object({
    summarySource: z.string().min(1, 'summarySource обязателен'),
    fields: z.array(JiraFieldMappingEntrySchema),
});
// ─── Граф workflow-переходов (многошаговая смена статуса) ─────────────────
/** Ребро графа переходов: из статуса fromStatus можно перейти в toStatus. */
export const JiraTransitionEdgeSchema = z.object({
    fromStatus: z.string(),
    toStatus: z.string(),
    transitionName: z.string().nullable().optional(),
    /** Переход требует экран Jira — Lens не может выполнить его автоматически. */
    hasScreen: z.boolean(),
});
/** Узел диаграммы: статус Jira и ключ его категории (new/indeterminate/done). */
export const JiraTransitionNodeSchema = z.object({
    name: z.string(),
    category: z.string(),
});
/** Ответ на GET .../transition-graph: узлы, рёбра, целевые статусы маппинга. */
export const JiraTransitionGraphSchema = z.object({
    nodes: z.array(JiraTransitionNodeSchema),
    edges: z.array(JiraTransitionEdgeSchema),
    mappedTargets: z.array(z.string()),
});
/** Запрос на read-only скан workflow по задачам-образцам. */
export const JiraTransitionScanRequestSchema = z.object({
    sampleIssueKeys: z.array(z.string().min(1)).min(1).max(20),
});
export const JiraTransitionScanResponseSchema = z.object({
    scanned: z.number(),
    edges: z.array(JiraTransitionEdgeSchema),
    /** Статусы проекта, для которых не нашлось ни одной задачи (рёбра неизвестны). */
    missingStatuses: z.array(z.string()).optional(),
});
/** Направление синхронизации. push_only = только выгрузка, pull_only = только загрузка, both = в обе стороны. */
export const SyncDirectionSchema = z.enum(['push_only', 'pull_only', 'both']).default('push_only');
/**
 * Источник Jira projectKey при push/pull.
 * - `static` — берётся `jiraProjectKey` из самого конфига (текущее поведение).
 * - `field`  — резолвится из значения поля записи (text/computed/reference/select)
 *   на момент push'а. Позволяет в одном itemType отгружать записи в разные
 *   Jira-проекты в зависимости от атрибута сущности. Для `select`-поля
 *   опциональный `valueMap` позволяет задать соответствие «option.value → projectKey».
 *   Без `valueMap` для select берётся option.value как есть.
 */
export const JiraProjectKeySourceSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('static') }),
    z.object({
        kind: z.literal('field'),
        fieldDefinitionId: z.string().uuid(),
        /** Маппинг option.value → projectKey. Используется только если поле-источник имеет тип `select`. */
        valueMap: z.record(z.string(), z.string()).optional(),
    }),
]);
/**
 * Источник Jira issueType при push/pull.
 * - `static` — берётся `jiraIssueType` из самого конфига (текущее поведение).
 * - `field`  — резолвится из значения select-поля записи через `valueMap`
 *   (option.value → имя issue type'а в Jira). Без `valueMap` берётся option.value.
 *   Поддерживается ТОЛЬКО select-поле (для остальных типов смысла нет).
 */
export const JiraIssueTypeSourceSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('static') }),
    z.object({
        kind: z.literal('field'),
        fieldDefinitionId: z.string().uuid(),
        /** Маппинг option.value → имя Jira issue type'а. */
        valueMap: z.record(z.string(), z.string()).optional(),
    }),
]);
export const JiraSyncConfigSettingsSchema = z
    .object({
    /** Источник projectKey при push. Опционально; отсутствует == `{ kind: 'static' }`. */
    projectKeySource: JiraProjectKeySourceSchema.optional(),
    /** Источник issueType при push. Опционально; отсутствует == `{ kind: 'static' }`. */
    issueTypeSource: JiraIssueTypeSourceSchema.optional(),
    /** Список Jira-полей, отображаемых во вкладке «Jira» карточки записи. */
    displayFields: z.array(z.string()).optional(),
})
    .catchall(z.unknown());
export const CreateJiraSyncConfigRequestSchema = z.object({
    itemTypeId: z.string().uuid(),
    jiraProjectKey: z.string().min(1),
    jiraIssueType: z.string().min(1),
    fieldMapping: JiraFieldMappingSchema,
    syncDirection: SyncDirectionSchema.optional(),
    /** Раздельный маппинг для pull (Jira → Lens). Если не указан — при pull используется конверсия из push. */
    pullFieldMapping: z.lazy(() => JiraPullMappingSchema).optional().nullable(),
    settings: JiraSyncConfigSettingsSchema.optional(),
});
export const UpdateJiraSyncConfigRequestSchema = z.object({
    jiraProjectKey: z.string().min(1).optional(),
    jiraIssueType: z.string().min(1).optional(),
    fieldMapping: JiraFieldMappingSchema.optional(),
    syncDirection: SyncDirectionSchema.optional(),
    pullFieldMapping: z.lazy(() => JiraPullMappingSchema).optional().nullable(),
    settings: JiraSyncConfigSettingsSchema.optional(),
});
/** Копирование маппинга push и/или pull из другого itemType того же пространства. */
export const CopyJiraMappingRequestSchema = z.object({
    sourceItemTypeId: z.string().uuid(),
    /**
     * push / pull / both — копируется одноимённый маппинг.
     * pushFromPull — целевой push заполняется из исходного pull (с обратной конверсией трансформаций).
     * pullFromPush — целевой pull заполняется из исходного push (с конверсией трансформаций; fixed-строки пропускаются).
     */
    direction: z
        .enum(['push', 'pull', 'both', 'pushFromPull', 'pullFromPush'])
        .default('both'),
});
// ─── Jira Sync Config DTO ────────────────────────────────────────────────
export const JiraSyncConfigSchema = z.object({
    id: z.string().uuid(),
    spaceId: z.string().uuid(),
    itemTypeId: z.string().uuid(),
    jiraProjectKey: z.string(),
    jiraIssueType: z.string(),
    fieldMapping: JiraFieldMappingSchema,
    /** Раздельный маппинг pull; null — используется конверсия из push. */
    pullFieldMapping: z.lazy(() => JiraPullMappingSchema).nullable(),
    syncDirection: SyncDirectionSchema,
    settings: JiraSyncConfigSettingsSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
});
// ─── Jira Push Response ──────────────────────────────────────────────────
export const JiraPushWarningSchema = z.object({
    code: z.string(),
    message: z.string(),
    jiraField: z.string().optional(),
});
export const JiraPushResponseSchema = z.object({
    jiraIssueKey: z.string(),
    jiraStatus: z.string().nullable(),
    jiraSyncedAt: z.string(),
    action: z.enum(['created', 'updated']),
    /** Не-фатальные предупреждения (option-mismatch, недоступная transition и т.п.). */
    warnings: z.array(JiraPushWarningSchema).optional(),
});
// ─── Jira Sync Log DTO ──────────────────────────────────────────────────
export const JiraSyncLogSchema = z.object({
    id: z.string().uuid(),
    syncConfigId: z.string().uuid(),
    itemId: z.string().uuid().nullable(),
    syncedById: z.string().uuid(),
    jiraIssueKey: z.string().nullable(),
    status: z.enum(['success', 'error']),
    details: z.unknown(),
    syncedAt: z.string(),
});
// ─── Bulk push request ──────────────────────────────────────────────────
/**
 * Опциональные фильтры полей для bulk push.
 * - includeFieldSlugs — глобальный allowlist полей по slug (Lens). Если задан,
 *   в push идут только перечисленные поля (плюс summarySource всегда). Поля,
 *   не входящие в список, не отправляются в Jira (даже если в маппинге есть).
 *   Если не задан — берутся все поля из маппинга (старое поведение).
 * - excludeFieldSlugsByItem — точечные снятия конкретных полей по каждому item.
 *   Применяется поверх includeFieldSlugs.
 */
export const BulkJiraPushFieldFilterSchema = z.object({
    includeFieldSlugs: z.array(z.string().min(1)).optional(),
    excludeFieldSlugsByItem: z
        .record(z.string().uuid(), z.array(z.string().min(1)))
        .optional(),
});
const BulkJiraPushTargetSchema = z.union([
    z.object({ itemIds: z.array(z.string().uuid()).min(1).max(1000) }),
    z.object({ viewId: z.string().uuid() }),
]);
export const BulkJiraPushRequestSchema = z.intersection(BulkJiraPushTargetSchema, BulkJiraPushFieldFilterSchema);
// ─── Bulk pull request (новый — для Sync Inbox pull-режима) ──────────────
export const BulkJiraPullRequestSchema = z.object({
    /** Список itemId для pull. Все должны быть уже связаны с Jira (jiraIssueKey != null). */
    itemIds: z.array(z.string().uuid()).min(1).max(1000),
    /** Глобальный allowlist полей (по slug Lens). Если задан — берутся только эти поля. */
    includeFieldSlugs: z.array(z.string().min(1)).optional(),
    /** Точечные исключения по itemId. */
    excludeFieldSlugsByItem: z
        .record(z.string().uuid(), z.array(z.string().min(1)))
        .optional(),
});
// ─── Sync Inbox (новые контракты для view-level previewer) ──────────────
export const SyncInboxItemSchema = z.object({
    itemId: z.string().uuid(),
    title: z.string(),
    jiraIssueKey: z.string().nullable(),
    action: z.enum(['create', 'update', 'no_change', 'unlinked']),
    changedFieldCount: z.number().int(),
    changedFieldSlugs: z.array(z.string()),
    errorMessage: z.string().optional(),
});
export const SyncInboxResponseSchema = z.object({
    direction: z.enum(['push', 'pull']),
    totalItems: z.number().int(),
    unlinked: z.number().int(),
    toCreate: z.number().int(),
    toUpdate: z.number().int(),
    noChange: z.number().int(),
    errors: z.number().int(),
    items: z.array(SyncInboxItemSchema),
    availableFields: z.array(z.object({ slug: z.string(), name: z.string() })),
    warnings: z.array(z.string()),
});
export const SyncInboxRequestSchema = z.object({
    direction: z.enum(['push', 'pull']),
    includeFieldSlugs: z.array(z.string().min(1)).optional(),
});
// ─── Per-record Sync Diff (drill-down) ──────────────────────────────────
export const PushDiffFieldSchema = z.object({
    lensFieldSlug: z.string().nullable(),
    lensFieldName: z.string().nullable(),
    jiraField: z.string(),
    /** Человекочитаемое имя Jira-поля (из listAllFields); null если не разрешилось. */
    jiraFieldName: z.string().nullable().optional(),
    currentJiraValue: z.unknown(),
    newJiraValue: z.unknown(),
    willChange: z.boolean(),
    status: z.enum(['ok', 'warning', 'error']),
    message: z.string().nullable(),
});
export const PushDiffResponseSchema = z.object({
    itemId: z.string().uuid(),
    itemTitle: z.string(),
    jiraIssueKey: z.string().nullable(),
    action: z.enum(['create', 'update', 'no_change']),
    fields: z.array(PushDiffFieldSchema),
    changedFieldCount: z.number().int(),
    errors: z.number().int(),
    warnings: z.number().int(),
    canApply: z.boolean(),
});
export const PullDiffFieldSchema = z.object({
    jiraField: z.string(),
    /** Человекочитаемое имя Jira-поля (из listAllFields); null если не разрешилось. */
    jiraFieldName: z.string().nullable().optional(),
    lensFieldSlug: z.string(),
    lensFieldName: z.string().nullable(),
    jiraValue: z.unknown(),
    currentLensValue: z.unknown(),
    newLensValue: z.unknown(),
    willChange: z.boolean(),
    status: z.enum(['ok', 'warning', 'error']),
    message: z.string().nullable(),
});
export const PullDiffResponseSchema = z.object({
    itemId: z.string().uuid(),
    itemTitle: z.string(),
    jiraIssueKey: z.string(),
    action: z.enum(['update', 'no_change']),
    fields: z.array(PullDiffFieldSchema),
    changedFieldCount: z.number().int(),
    errors: z.number().int(),
    warnings: z.number().int(),
    canApply: z.boolean(),
});
// ─── Links Sync (отдельный режим в Sync Inbox) ──────────────────────────
export const LinksInboxItemSchema = z.object({
    itemId: z.string().uuid(),
    title: z.string(),
    jiraIssueKey: z.string().nullable(),
    actionableCount: z.number().int(),
    syncedCount: z.number().int(),
    unsyncableCount: z.number().int(),
    errorMessage: z.string().optional(),
});
export const LinksInboxResponseSchema = z.object({
    direction: z.enum(['push', 'pull']),
    totalItems: z.number().int(),
    totalActionable: z.number().int(),
    totalSynced: z.number().int(),
    totalUnsyncable: z.number().int(),
    errors: z.number().int(),
    items: z.array(LinksInboxItemSchema),
    availableLinkTypes: z.array(z.string()),
    warnings: z.array(z.string()),
});
export const LinksInboxRequestSchema = z.object({
    includeLinkTypes: z.array(z.string().min(1)).optional(),
});
export const LinksPushDiffEntrySchema = z.object({
    linkId: z.string().uuid(),
    targetItemId: z.string().uuid(),
    targetJiraIssueKey: z.string().nullable(),
    targetTitle: z.string(),
    linkType: z.string(),
    state: z.enum(['in_jira', 'not_in_jira', 'unsyncable']),
    canPush: z.boolean(),
    message: z.string().optional(),
});
export const LinksPushDiffResponseSchema = z.object({
    itemId: z.string().uuid(),
    itemTitle: z.string(),
    jiraIssueKey: z.string().nullable(),
    links: z.array(LinksPushDiffEntrySchema),
    toPush: z.number().int(),
    inJira: z.number().int(),
    unsyncable: z.number().int(),
});
export const LinksPullDiffEntrySchema = z.object({
    otherJiraIssueKey: z.string(),
    otherItemId: z.string().uuid().nullable(),
    otherTitle: z.string().nullable(),
    linkType: z.string(),
    direction: z.enum(['outward', 'inward']),
    state: z.enum(['in_lens', 'not_in_lens', 'unsyncable']),
    canPull: z.boolean(),
    message: z.string().optional(),
});
/** Lens-связь, которую Jira уже не содержит — кандидат на удаление при pull. */
export const LinksOrphanEntrySchema = z.object({
    linkId: z.string().uuid(),
    otherItemId: z.string().uuid(),
    otherJiraIssueKey: z.string(),
    otherTitle: z.string(),
    linkType: z.string(),
    direction: z.enum(['outward', 'inward']),
});
export const LinksPullDiffResponseSchema = z.object({
    itemId: z.string().uuid(),
    itemTitle: z.string(),
    jiraIssueKey: z.string(),
    links: z.array(LinksPullDiffEntrySchema),
    toPull: z.number().int(),
    inLens: z.number().int(),
    unsyncable: z.number().int(),
    orphans: z.array(LinksOrphanEntrySchema).default([]),
});
/**
 * Bulk push/pull связей. Берёт список itemIds, для каждого item процессит все
 * outgoing-связи (push) или все Jira issuelinks (pull).
 * - includeLinkTypes — глобальный фильтр по типам связи.
 * - selections — точечный per-item per-link выбор. Если задан, для записи
 *   обрабатываются только указанные связи. Если не задан для какого-то itemId —
 *   обрабатываются все «can*» связи (старое поведение).
 */
export const LinksPushSelectionSchema = z.object({
    /** id записей item_link, которые нужно push'ить. */
    linkIds: z.array(z.string().uuid()),
});
export const LinksPullSelectionSchema = z.object({
    /** Уникальные тройки `(otherJiraKey, linkType, direction)` для pull. */
    entries: z.array(z.object({
        otherJiraKey: z.string(),
        linkType: z.string(),
        direction: z.enum(['outward', 'inward']),
    })),
});
export const BulkLinksSyncRequestSchema = z.object({
    itemIds: z.array(z.string().uuid()).min(1).max(1000),
    direction: z.enum(['push', 'pull']),
    includeLinkTypes: z.array(z.string().min(1)).optional(),
    /** Push: per-item allowlist linkIds. Pull: per-item allowlist (key, type, direction). */
    selections: z
        .record(z.string().uuid(), z.union([LinksPushSelectionSchema, LinksPullSelectionSchema]))
        .optional(),
});
export const BulkJiraSyncBothRequestSchema = z.object({
    itemIds: z.array(z.string().uuid()).min(1).max(1000),
    direction: z.enum(['push', 'pull']),
    includeFieldSlugs: z.array(z.string().min(1)).optional(),
    excludeFieldSlugsByItem: z.record(z.string().uuid(), z.array(z.string().min(1))).optional(),
    includeLinkTypes: z.array(z.string().min(1)).optional(),
    selections: z.record(z.string().uuid(), z.union([LinksPushSelectionSchema, LinksPullSelectionSchema])).optional(),
});
// ─── Mapping Templates ──────────────────────────────────────────────────
export const CreateMappingTemplateRequestSchema = z.object({
    name: z.string().trim().min(1).max(200),
    description: z.string().max(1000).optional(),
    jiraProjectPattern: z.string().max(100).optional(),
    jiraIssueType: z.string().max(100).optional(),
    fieldMapping: JiraFieldMappingSchema,
});
export const MappingTemplateSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable(),
    isBuiltin: z.boolean(),
    jiraProjectPattern: z.string().nullable(),
    jiraIssueType: z.string().nullable(),
    fieldMapping: JiraFieldMappingSchema,
    createdById: z.string().uuid().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
// ─── Jira item status (inline in item DTO) ───────────────────────────────
export const JiraItemStatusSchema = z.object({
    jiraIssueKey: z.string().nullable(),
    jiraStatus: z.string().nullable(),
    jiraSyncedAt: z.string().nullable(),
});
// ─── Lens item links ────────────────────────────────────────────────────
export const ItemLinkRecordSchema = z.object({
    id: z.string().uuid(),
    sourceItemId: z.string().uuid(),
    targetItemId: z.string().uuid(),
    fieldDefinitionId: z.string().uuid().nullable(),
    linkType: z.string(),
    createdById: z.string().uuid().nullable(),
    createdAt: z.string(),
});
export const ItemLinkSchema = z.object({
    id: z.string().uuid(),
    linkId: z.string().uuid(),
    fieldDefinitionId: z.string().uuid().nullable(),
    fieldName: z.string().min(1),
    linkType: z.string(),
    direction: z.enum(['outgoing', 'incoming']),
    title: z.string(),
    fields: z.record(z.string(), z.unknown()),
    jiraIssueKey: z.string().nullable(),
    jiraStatus: z.string().nullable(),
});
export const CreateItemLinkBodySchema = z.object({
    fieldDefinitionId: z.string().uuid().optional(),
    linkType: z.string().default('Relates'),
    targetItemId: z.string().uuid(),
});
export const LinksListResponseSchema = z.object({
    items: z.array(ItemLinkSchema),
});
// ─── Pull Diagnostics ──────────────────────────────────────────────────
/** Пропущенное поле при pull. */
export const PullSkippedFieldSchema = z.object({
    jiraKey: z.string(),
    jiraField: z.string(),
    reason: z.enum([
        'no_mapping', // Jira-поле не замаплено
        'lens_field_not_found', // activitiesFieldSlug не найден в item_type
        'transform_failed', // reverse transform не смог преобразовать
        'jira_value_null', // значение в Jira пустое
        'jira_status_not_in_value_map', // Jira status.name не найден в valueMap status_map_reverse
    ]),
    jiraValue: z.unknown().optional(),
    message: z.string(),
});
// ─── Pull from Jira (Wave 1+2) ─────────────────────────────────────────
/** Маппинг полей для pull (Jira → Lens). */
export const JiraPullFieldMappingEntrySchema = z.object({
    jiraField: z.string(),
    activitiesFieldSlug: z.string(),
    transform: z.enum([
        'none',
        'reference_key_reverse', // логин → entry_id справочника
        'jira_wiki_to_markdown', // Jira wiki → markdown
        'jira_select_value', // { value: "X" } → "X"
        'status_map_reverse', // Jira status.name -> Lens option id через valueMap
        'bool_map_reverse', // Jira option value -> boolean true/false через valueMap
    ]).default('none'),
    /** Маппинг ключа значения -> Jira value (status: optionId→name; bool: 'true'/'false'→option). */
    valueMap: z.record(z.string(), z.string()).optional(),
});
export const JiraPullMappingSchema = z.object({
    summaryTarget: z.string().min(1).default('title'),
    fields: z.array(JiraPullFieldMappingEntrySchema),
});
/** Режим pull. */
export const JiraPullModeSchema = z.enum(['create_only', 'create_and_update']);
/** Запрос на preview pull. */
export const JiraPullPreviewRequestSchema = z.object({
    jql: z.string().min(1).max(2000),
    itemTypeId: z.string().uuid(),
    fieldMapping: JiraPullMappingSchema,
    mode: JiraPullModeSchema.default('create_only'),
    maxResults: z.number().int().min(1).max(1000).default(100),
});
/** Строка preview: что произойдёт с каждым issue. */
export const JiraPullPreviewItemSchema = z.object({
    jiraKey: z.string(),
    summary: z.string(),
    action: z.enum(['create', 'update', 'skip', 'no_changes']),
    existingItemId: z.string().uuid().nullable(),
    /** Поля, которые изменятся (только для update). */
    changedFields: z.array(z.string()).optional(),
});
/** Превью значений полей для одной выборочной задачи (как заполнится в Lens). */
export const JiraPullFieldSamplePreviewSchema = z.object({
    jiraField: z.string(),
    activitiesFieldSlug: z.string(),
    activitiesFieldName: z.string().nullable(),
    activitiesFieldType: z.string().nullable(),
    transform: z.string(),
    jiraValue: z.unknown(),
    /** Сериализованное значение, которое попадёт в Lens (string для отображения). */
    resolvedValue: z.string().nullable(),
    /** Если поле не попадёт в Lens — причина и человекочитаемое сообщение. */
    status: z.enum(['ok', 'skipped', 'error']),
    message: z.string().nullable(),
});
export const JiraPullSamplePreviewSchema = z.object({
    jiraKey: z.string(),
    summary: z.string(),
    fields: z.array(JiraPullFieldSamplePreviewSchema),
});
/** Ответ preview. */
export const JiraPullPreviewResponseSchema = z.object({
    total: z.number().int(),
    items: z.array(JiraPullPreviewItemSchema),
    stats: z.object({
        create: z.number().int(),
        update: z.number().int(),
        skip: z.number().int(),
        noChanges: z.number().int(),
    }),
    /** Поля, которые были пропущены при маппинге (диагностика). */
    skippedFields: z.array(PullSkippedFieldSchema).optional(),
    /** Превью «как заполнится» по нескольким первым задачам. */
    samplePreview: z.array(JiraPullSamplePreviewSchema).optional(),
});
/** Запрос на выполнение pull. */
export const JiraPullExecuteRequestSchema = z.object({
    jql: z.string().min(1).max(2000),
    itemTypeId: z.string().uuid(),
    fieldMapping: JiraPullMappingSchema,
    mode: JiraPullModeSchema.default('create_only'),
    /** Ключи issues для импорта (если пусто — все). */
    selectedKeys: z.array(z.string()).optional(),
});
/** Результат выполнения pull. */
export const JiraPullExecuteResponseSchema = z.object({
    created: z.number().int(),
    updated: z.number().int(),
    skipped: z.number().int(),
    errors: z.array(z.object({
        jiraKey: z.string(),
        error: z.string(),
    })),
    /** Поля, которые были пропущены при маппинге (диагностика). */
    skippedFields: z.array(PullSkippedFieldSchema).optional(),
});
/** Сохранённый запрос pull. */
export const JiraSavedPullQuerySchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1).max(200),
    jql: z.string().min(1).max(2000),
    itemTypeId: z.string().uuid(),
    fieldMapping: JiraPullMappingSchema,
    mode: JiraPullModeSchema,
});
// ─── Weight validation (Wave 1) ─────────────────────────────────────────
/** Результат валидации весов одного сотрудника. */
export const WeightValidationEntrySchema = z.object({
    personEntryId: z.string().uuid(),
    personName: z.string(),
    totalWeight: z.number(),
    isValid: z.boolean(),
    goalCount: z.number().int(),
    /** Отдельные проблемы. */
    issues: z.array(z.enum([
        'sum_not_100', // сумма ≠ 100
        'weight_not_multiple_5', // вес не кратен 5
        'no_goals', // нет целей
    ])),
});
/** Запрос валидации весов. */
export const WeightValidationRequestSchema = z.object({
    itemTypeId: z.string().uuid(),
    quarter: z.string().min(1).max(20),
});
/** Ответ валидации весов. */
export const WeightValidationResponseSchema = z.object({
    entries: z.array(WeightValidationEntrySchema),
    allValid: z.boolean(),
    stats: z.object({
        totalPeople: z.number().int(),
        validPeople: z.number().int(),
        invalidPeople: z.number().int(),
    }),
});
// ─── Push Preview (dry-run трансформации одной записи) ─────────────────
/** Результат трансформации одного поля при preview. */
export const PushPreviewFieldSchema = z.object({
    /** Slug поля Lens (или null для fixed-значений). */
    lensFieldSlug: z.string().nullable(),
    lensFieldName: z.string().nullable(),
    /** Исходное значение из Lens (человекочитаемое). */
    lensValue: z.unknown(),
    /** Ключ Jira-поля. */
    jiraField: z.string(),
    jiraFieldName: z.string().nullable(),
    /** Значение, которое будет отправлено в Jira. */
    jiraValue: z.unknown(),
    /** Промежуточные шаги трансформации (для reference_key и т.п.). */
    transformSteps: z.array(z.string()).optional(),
    status: z.enum(['ok', 'warning', 'error']),
    /** Сообщение (для warning/error). */
    message: z.string().nullable(),
});
/** Ответ preview push. */
export const PushPreviewResponseSchema = z.object({
    itemId: z.string(),
    itemTitle: z.string(),
    /** Итоговый payload, который был бы отправлен в Jira. */
    jiraPayload: z.record(z.unknown()),
    /** Детальная диагностика по каждому полю маппинга. */
    fields: z.array(PushPreviewFieldSchema),
    /** Общий статус: можно ли отправить. */
    canPush: z.boolean(),
    errors: z.number().int(),
    warnings: z.number().int(),
});
/**
 * Запрос preview push.
 * - `itemId` — опционален; без него берётся первая запись item_type'а.
 * - `draft` — опциональный draft-маппинг: если передан, preview применяет его
 *   вместо сохранённого конфига. Нужен, чтобы тестировать маппинг до save.
 */
export const PushPreviewRequestSchema = z.object({
    itemId: z.string().uuid().optional(),
    draft: z
        .object({
        fieldMapping: JiraFieldMappingSchema,
    })
        .optional(),
});
const LinkSyncRunErrorSchema = z.object({
    itemId: z.string().uuid().optional(),
    jiraIssueKey: z.string().optional(),
    error: z.string(),
});
/** Результат ручного запуска push cross_link → Jira issueLink. */
export const JiraLinkPushRunResponseSchema = z.object({
    processed: z.number().int(),
    linksCreated: z.number().int(),
    skipped: z.number().int(),
    errors: z.array(LinkSyncRunErrorSchema),
});
/** Результат ручного pull Jira issueLinks в Lens item_links. */
export const JiraLinkPullRunResponseSchema = z.object({
    processed: z.number().int(),
    linksSynced: z.number().int(),
    skipped: z.number().int(),
    errors: z.array(LinkSyncRunErrorSchema),
});
/** Обобщённый ответ ручного запуска link-sync endpoint'ов. */
export const LinkSyncRunResponseSchema = z.union([
    JiraLinkPushRunResponseSchema,
    JiraLinkPullRunResponseSchema,
]);
// Раздел «Авто-синхронизация с Jira» удалён в FR-12 unification:
// все pull/push по cron'у теперь живут как шаги jira.pull / jira.push
// в правилах автоматизации (см. packages/shared/src/schemas/automation.ts).
// ─── Создание ItemType из примера Jira-задач ─────────────────────────────
//
// Визард «Создать тип сущности из примера Jira-задачи»: пользователь даёт
// 1..N issueKeys, бэкенд тянет issue + project statuses, маппер строит
// черновик ItemType (поля + предложенные справочники + статусное поле),
// фронт даёт пользователю отредактировать draft, затем `commit` создаёт
// ItemType, FieldDefinition'ы, недостающие справочники и JiraSyncConfig
// с заполненным fieldMapping.
/** Тип поля в черновике; `status` отдельный т.к. требует hasCategories+valueMap для push. `skip` — поле не импортировать. */
export const DraftFieldKindSchema = z.enum([
    'text',
    'textarea',
    'markdown',
    'number',
    'date',
    'select',
    'multi_select',
    'reference',
    'url',
    'boolean',
    'status',
    'skip',
]);
/** Опция select/multi_select/status в черновике. Для status `jiraStatusName`/`jiraStatusId` нужны для построения valueMap при commit. */
export const DraftSelectOptionSchema = z.object({
    value: z.string().min(1),
    label: z.string().min(1),
    category: z.enum(['backlog', 'active', 'done', 'cancelled']).optional(),
    jiraStatusId: z.string().optional(),
    jiraStatusName: z.string().optional(),
});
/** Колонка предложенного справочника (минимум 'name'; для людей добавим 'displayName' и 'email'). */
export const ProposedDictionaryColumnSchema = z.object({
    slug: z.string().min(1),
    name: z.string().min(1),
    type: z.enum(['text', 'number']).default('text'),
});
/** Предложенный справочник. `refKey` — id внутри draft; на него ссылается DraftField.dictionaryRef. */
export const ProposedDictionarySchema = z.object({
    refKey: z.string().min(1),
    name: z.string().min(1),
    keyColumn: z.string().min(1),
    displayColumn: z.string().min(1),
    columns: z.array(ProposedDictionaryColumnSchema).min(1),
    /** Сэмплы entries из исследованных issue (key → колонки). Используются для предзаполнения справочника при commit. */
    sampleEntries: z.array(z.record(z.string(), z.union([z.string(), z.number(), z.null()]))).default([]),
});
/** Ссылка из reference-поля на справочник: либо существующий, либо предложенный (refKey ProposedDictionary). */
export const DraftDictionaryRefSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('existing'), dictionaryId: z.string().uuid() }),
    z.object({ kind: z.literal('proposed'), refKey: z.string().min(1) }),
]);
/** Поле черновика. Для commit нужны: fieldType + (options | dictionaryRef) + jiraTransform для маппинга. */
export const DraftFieldSchema = z.object({
    /** Системное имя Jira-поля: 'summary'/'assignee'/'customfield_12345'/'status'. */
    jiraField: z.string().min(1),
    /** Человекочитаемое имя (из Jira `names`). */
    jiraFieldName: z.string().min(1),
    /** schema из Jira issue (type/items/custom) — для отладки и хейристик. */
    jiraSchema: z.object({
        type: z.string(),
        items: z.string().optional(),
        custom: z.string().optional(),
    }),
    suggestedSlug: z.string().min(1),
    suggestedName: z.string().min(1),
    fieldType: DraftFieldKindSchema,
    /** Для select/multi_select/status. */
    options: z.array(DraftSelectOptionSchema).optional(),
    /** Только для status: всегда true (FR-3.2). */
    hasCategories: z.boolean().optional(),
    /** Для reference. */
    dictionaryRef: DraftDictionaryRefSchema.optional(),
    /** Для reference: множественный выбор (Jira-поле массивное: components, fixVersions, multi-user и т.п.). */
    multiple: z.boolean().optional(),
    /** Помечает поле как название записи (`itemTypes.titleFieldId`). Должно быть ровно одно. */
    isTitle: z.boolean().default(false),
    /** Не импортировать поле (но оставить в draft для контекста UI). */
    skip: z.boolean().default(false),
    warnings: z.array(z.string()).default([]),
    /** Трансформация при push в Jira; для status — 'status_map'. */
    jiraTransform: z.enum(['none', 'reference_key', 'markdown_to_jira_wiki', 'status_map', 'bool_map']).default('none'),
    /** Сэмплы значений из исследованных issue (для подсветки в UI). */
    sampleValues: z.array(z.unknown()).default([]),
});
export const ItemTypeFromJiraSampleDraftSchema = z.object({
    itemTypeName: z.string().min(1),
    itemTypeSlug: z.string().min(1),
    jiraProjectKey: z.string().min(1),
    jiraIssueType: z.string().min(1),
    fields: z.array(DraftFieldSchema),
    proposedDictionaries: z.array(ProposedDictionarySchema),
    /** jiraField статусного поля ('status' или customfield_*); null если статусы решено не импортировать. */
    statusFieldRef: z.string().nullable(),
    /** Глобальные предупреждения (несколько issuetype в выборке, отсутствие issueKey и т.п.). */
    warnings: z.array(z.string()).default([]),
    sampleIssueKeys: z.array(z.string().min(1)),
});
export const ItemTypeFromJiraSamplePreviewRequestSchema = z.object({
    issueKeys: z.array(z.string().min(1)).min(1).max(20),
});
export const ItemTypeFromJiraSamplePreviewResponseSchema = z.object({
    draft: ItemTypeFromJiraSampleDraftSchema,
});
export const ItemTypeFromJiraSampleCommitRequestSchema = z.object({
    draft: ItemTypeFromJiraSampleDraftSchema,
});
export const ItemTypeFromJiraSampleCommitResponseSchema = z.object({
    itemTypeId: z.string().uuid(),
    jiraSyncConfigId: z.string().uuid(),
    createdDictionaryIds: z.array(z.string().uuid()),
});
// ─── Добавление полей в существующий ItemType из Jira-задач ──────────────
export const AddFieldsFromJiraPreviewRequestSchema = z.object({
    issueKeys: z.array(z.string().min(1)).min(1).max(20),
});
export const AddFieldsFromJiraPreviewResponseSchema = z.object({
    draft: ItemTypeFromJiraSampleDraftSchema,
    existingFieldSlugs: z.array(z.string()),
    hasJiraSyncConfig: z.boolean(),
    hasStatusField: z.boolean(),
});
export const AddFieldsFromJiraCommitRequestSchema = z.object({
    draft: ItemTypeFromJiraSampleDraftSchema,
});
export const AddFieldsFromJiraCommitResponseSchema = z.object({
    itemTypeId: z.string().uuid(),
    jiraSyncConfigId: z.string().uuid().nullable(),
    createdFieldIds: z.array(z.string().uuid()),
    createdDictionaryIds: z.array(z.string().uuid()),
});
// ─── Ручная связка записи с существующей задачей Jira ──────────────────────
export const LinkJiraIssueRequestSchema = z.object({
    jiraIssueKey: z.string().trim().min(1).max(64),
});
export const LinkJiraIssueResponseSchema = z.object({
    jiraIssueKey: z.string(),
    jiraStatus: z.string().nullable(),
    jiraSyncedAt: z.string().nullable(),
});
export const UnlinkJiraIssueResponseSchema = z.object({
    jiraIssueKey: z.null(),
});
